import { Column, GridContainer, Row } from '@vp/swan';
import { MPVIDS_WITH_TIERED_OPTIONS } from '@99designs/design-services-common';
import { useInVistaExperimentVariant } from '@99designs/experiments';
import { SupportedLocale } from '@99designs/i18n';
import { NewDesignProductPresenter } from '../../lib/presenter/productsPresenter';
import { getPriceForProduct } from '../../lib/pricing';
import { useProductPricingContext } from '../ProductPricingContext';
import { ProductTile } from '../ProductTile';
import type { NavigationTracking } from '@99designs/tracking';
import type { DesignProduct, DesignProductPresenter } from '../../lib/presenter/types';

interface ProductGridProps {
    products: DesignProduct[];
    locale: SupportedLocale;
    productClickedTrackingData?: (
        product: DesignProductPresenter,
        index: number
    ) => NavigationTracking;
    SeeAllTile?: React.ReactNode;
}

export function ProductGrid({
    products,
    locale,
    productClickedTrackingData,
    SeeAllTile,
}: ProductGridProps) {
    const inVariant = useInVistaExperimentVariant('design_services_sdp_turnaround_time');
    const { productPrices, priceLoading } = useProductPricingContext();

    return (
        <GridContainer>
            <Row verticalGap="matchGutter">
                {products.map((product, index) => {
                    const presenter = NewDesignProductPresenter(product);
                    const productPrice = getPriceForProduct(product, productPrices);
                    const isTieringProduct =
                        locale === 'en-US' &&
                        MPVIDS_WITH_TIERED_OPTIONS[locale].includes(product.mpvId.toLowerCase()) &&
                        (inVariant('variant_24_hour') || inVariant('variant_3_day'));

                    return (
                        <Column
                            key={presenter.mpvId}
                            data-testid="product-grid-column"
                            span={3}
                            spanXs={6}
                            spanSm={3}
                        >
                            <ProductTile
                                key={presenter.mpvId}
                                title={presenter.name}
                                imageSrc={
                                    presenter.previewImage({ cloudinaryOptions: { width: 450 } })
                                        .url
                                }
                                imageAspectRatio={
                                    presenter.previewImage({ cloudinaryOptions: { width: 450 } })
                                        .aspectRatio
                                }
                                ctaLink={presenter.ctaUrl(locale)}
                                trackingData={
                                    productClickedTrackingData
                                        ? productClickedTrackingData(presenter, index)
                                        : undefined
                                }
                                price={productPrice}
                                priceLoading={priceLoading}
                                isTieringProduct={isTieringProduct}
                            />
                        </Column>
                    );
                })}
                {SeeAllTile && (
                    <Column
                        key="see-all"
                        data-testid="product-grid-column"
                        span={3}
                        spanXs={6}
                        spanSm={3}
                    >
                        {SeeAllTile}
                    </Column>
                )}
            </Row>
        </GridContainer>
    );
}
